/**
 * « Autour de moi » pour les guides de voyage (le serveur IA dit pour quels livres), repris de Studio : la position
 * de l'appareil, nommée (rue, quartier, ville) pour que les passages du guide sur le lieu soient trouvés, et la
 * question posée au livre.
 */
import { defineMessages, translator, type Locale } from "../i18n/index.ts";

const messages = defineMessages(
  {
    label: "Autour de moi",
    unavailable: "La localisation n'est pas disponible sur cet appareil.",
    denied: "La localisation est refusée : autorisez-la pour ce site dans les réglages du navigateur.",
    notFound: "Votre position n'a pas pu être trouvée. Réessayez dans un instant.",
    outside: "Vous n'êtes pas dans la zone couverte par ce guide ({area}) : « Autour de moi » n'est proposé que sur place.",
    questionAt: "à {name} ",
    question:
      "Autour de moi : je suis {at}(GPS {latitude}, {longitude}). " +
      "D'après le guide, que me conseilles-tu à proximité : à voir, où manger, où dormir ? " +
      "Commence par le plus proche et donne les adresses.",
  },
  {
    label: "Around me",
    unavailable: "Location is not available on this device.",
    denied: "Location access is blocked: allow it for this site in your browser settings.",
    notFound: "Your location could not be found. Try again in a moment.",
    outside: "You are not in the area this guide covers ({area}): “Around me” is only offered there.",
    questionAt: "in {name} ",
    question:
      "Around me: I am {at}(GPS {latitude}, {longitude}). " +
      "According to the guide, what do you recommend nearby: things to see, where to eat, where to stay? " +
      "Start with the closest and give the addresses.",
  },
);

export const aroundMeLabel = (locale: Locale) => translator(messages, locale)("label");

/** Erreur dite telle quelle au lecteur. */
export class AroundMeError extends Error {}

export interface Place {
  latitude: number;
  longitude: number;
  /** « Rue Fesch, Ajaccio, Corse-du-Sud » ; undefined quand la position n'a pas pu être nommée. */
  name?: string;
}

const currentPosition = (locale: Locale) =>
  new Promise<GeolocationPosition>((resolve, reject) => {
    const t = translator(messages, locale);
    if (typeof navigator === "undefined" || !navigator.geolocation) {
      reject(new AroundMeError(t("unavailable")));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      resolve,
      (error) => reject(new AroundMeError(t(error.code === error.PERMISSION_DENIED ? "denied" : "notFound"))),
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 60000 },
    );
  });

type Address = Record<string, string | undefined>;

/** Le nom d'un lieu d'après son adresse OpenStreetMap : de la rue à la région, sans doublon. */
export function placeName(address: Address): string | undefined {
  const parts = [
    address.road ?? address.pedestrian ?? address.square,
    address.suburb ?? address.neighbourhood ?? address.quarter,
    address.city ?? address.town ?? address.village ?? address.municipality,
    address.county ?? address.state,
    address.country,
  ].filter((part): part is string => !!part);
  const unique = parts.filter((part, index) => parts.indexOf(part) === index);
  return unique.length > 0 ? unique.join(", ") : undefined;
}

async function nameOf(latitude: number, longitude: number, locale: Locale): Promise<string | undefined> {
  try {
    const url = `https://nominatim.openstreetmap.org/reverse?format=jsonv2&zoom=17&accept-language=${locale}&lat=${latitude}&lon=${longitude}`;
    const response = await fetch(url, { headers: { Accept: "application/json" }, signal: AbortSignal.timeout(8000) });
    if (!response.ok) return undefined;
    const body = (await response.json()) as { address?: Address };
    return body.address ? placeName(body.address) : undefined;
  } catch {
    // Sans nom, seules les coordonnées partent.
    return undefined;
  }
}

export async function locate(locale: Locale): Promise<Place> {
  const { coords } = await currentPosition(locale);
  const latitude = Number(coords.latitude.toFixed(5));
  const longitude = Number(coords.longitude.toFixed(5));
  return { latitude, longitude, name: await nameOf(latitude, longitude, locale) };
}

/** Posée dans la langue de l'interface, que la réponse suit. */
export function aroundMeQuestion({ latitude, longitude, name }: Place, locale: Locale): string {
  const t = translator(messages, locale);
  return t("question", { at: name ? t("questionAt", { name }) : "", latitude, longitude });
}

/** Un lieu couvert par le guide, avec son rectangle (modules/book/guide_area.py). */
export interface AreaPlace {
  name: string;
  south: number;
  west: number;
  north: number;
  east: number;
}

/** La zone d'un guide ; `null` quand elle est inconnue (« Autour de moi » est alors proposé partout). */
export interface GuideArea {
  places: AreaPlace[];
  marginKm: number;
}

/** La réponse de `GET /server/book/product-id/{pid}/around-me/area`, ramenée à une zone ou à `null`. */
export function guideArea(data: { area?: { places?: AreaPlace[] } | null; margin_km?: number } | null | undefined): GuideArea | null {
  const places = data?.area?.places;
  return places?.length ? { places, marginKm: data?.margin_km ?? 10 } : null;
}

const distanceToBoxKm = (latitude: number, longitude: number, place: AreaPlace) => {
  const nearestLatitude = Math.min(Math.max(latitude, place.south), place.north);
  const nearestLongitude = Math.min(Math.max(longitude, place.west), place.east);
  const dy = (latitude - nearestLatitude) * 111.32;
  const dx = (longitude - nearestLongitude) * 111.32 * Math.cos((((latitude + nearestLatitude) / 2) * Math.PI) / 180);
  return Math.hypot(dx, dy);
};

/** Dans un des lieux du guide, ou près de l'un d'eux : la même règle que le serveur. */
export const inArea = (area: GuideArea, latitude: number, longitude: number) =>
  area.places.some((place) => distanceToBoxKm(latitude, longitude, place) <= area.marginKm);

/** Les noms de la zone, pour le lecteur : « Corse » pour « Corse, France ». */
export const areaNames = (area: GuideArea) => [...new Set(area.places.map((place) => place.name.split(",")[0]!.trim()))].join(", ");

/** Le lecteur est hors de la zone du guide. */
export const outsideMessage = (area: GuideArea, locale: Locale) => translator(messages, locale)("outside", { area: areaNames(area) });

/**
 * La position, cherchée dès l'ouverture d'un guide pour griser « Autour de moi » loin du guide avant qu'il serve :
 * le navigateur demande au lecteur s'il n'a pas encore répondu. Undefined si refusée (pas redemandée) ou introuvable :
 * « Autour de moi » reste alors proposé et vérifie la zone quand il sert.
 */
export async function positionAtOpening(): Promise<{ latitude: number; longitude: number } | undefined> {
  try {
    if (typeof navigator === "undefined" || !navigator.geolocation) return undefined;
    const permission = await navigator.permissions?.query({ name: "geolocation" }).catch(() => undefined);
    if (permission?.state === "denied") return undefined;
    const { coords } = await new Promise<GeolocationPosition>((resolve, reject) =>
      navigator.geolocation.getCurrentPosition(resolve, reject, { timeout: 30000, maximumAge: 300000 }),
    );
    return { latitude: coords.latitude, longitude: coords.longitude };
  } catch {
    return undefined;
  }
}
